import { ElementRef, Injectable } from '@angular/core';
import { GetHostedIframeContract } from '../api/contracts/get-hosted-iframe.contract';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import {
  AdyenPaymentMethod,
  GatewayPaymentMethod
} from '../pages/v2-order-pay/models/payment.model';
import { detect } from 'detect-browser';
import { IPaymentMethodsResponse } from '../api/contracts/payment-method-response.contracts';

@Injectable({
  providedIn: 'root'
})
export class AdyenStore {
  private readonly _footerGooglePayRef$ = new BehaviorSubject<
    ElementRef | undefined
  >(undefined);
  readonly footerGooglePayRef$ = this._footerGooglePayRef$.asObservable();

  set footerGooglePayRef(ref: ElementRef) {
    this._footerGooglePayRef$.next(ref);
  }

  private readonly _footerApplePayRef$ = new BehaviorSubject<
    ElementRef | undefined
  >(undefined);
  readonly footerApplePayRef$ = this._footerApplePayRef$.asObservable();

  set footerApplePayRef(ref: ElementRef) {
    this._footerApplePayRef$.next(ref);
  }

  private readonly _sheetGooglePayRef$ = new BehaviorSubject<
    ElementRef | undefined
  >(undefined);
  readonly sheetGooglePayRef$ = this._sheetGooglePayRef$.asObservable();

  set sheetGooglePayRef(ref: ElementRef) {
    this._sheetGooglePayRef$.next(ref);
  }

  private readonly _sheetApplePayRef$ = new BehaviorSubject<
    ElementRef | undefined
  >(undefined);
  readonly sheetApplePayRef$ = this._sheetApplePayRef$.asObservable();

  set sheetApplePayRef(ref: ElementRef) {
    this._sheetApplePayRef$.next(ref);
  }

  private readonly _isGooglePayFooterButtonLoading$ = new BehaviorSubject(true);
  readonly isGooglePayFooterButtonLoading$ =
    this._isGooglePayFooterButtonLoading$.asObservable();

  set isGooglePayFooterButtonLoading(v: boolean) {
    this._isGooglePayFooterButtonLoading$.next(v);
  }

  private readonly _isGooglePaySheetButtonLoading$ = new BehaviorSubject(true);
  readonly isGooglePaySheetButtonLoading$ =
    this._isGooglePaySheetButtonLoading$.asObservable();

  set isGooglePaySheetButtonLoading(v: boolean) {
    this._isGooglePaySheetButtonLoading$.next(v);
  }

  private readonly _isApplePayFooterButtonLoading$ = new BehaviorSubject(true);
  readonly isApplePayFooterButtonLoading$ =
    this._isApplePayFooterButtonLoading$.asObservable();

  set isApplePayFooterButtonLoading(v: boolean) {
    this._isApplePayFooterButtonLoading$.next(v);
  }

  private readonly _isApplePaySheetButtonLoading$ = new BehaviorSubject(true);
  readonly isApplePaySheetButtonLoading$ =
    this._isApplePaySheetButtonLoading$.asObservable();

  set isApplePaySheetButtonLoading(v: boolean) {
    this._isApplePaySheetButtonLoading$.next(v);
  }

  private readonly _isSheetActive$ = new BehaviorSubject(false);
  readonly isSheetActive$ = this._isSheetActive$.asObservable();

  get isSheetActive(): boolean {
    return this._isSheetActive$.getValue();
  }

  set isSheetActive(v: boolean) {
    this._isSheetActive$.next(v);
  }

  private readonly _amount$ = new BehaviorSubject(0);
  readonly amount$ = this._amount$.asObservable();

  get amount(): number {
    return this._amount$.getValue();
  }
  set amount(value: number) {
    this._amount$.next(value);
  }

  private readonly _isGooglePayAvailable$ = new BehaviorSubject(false);
  readonly isGooglePayAvailable$ = this._isGooglePayAvailable$.asObservable();

  get isGooglePayAvailable(): boolean {
    return this._isGooglePayAvailable$.getValue();
  }

  set isGooglePayAvailable(value: boolean) {
    this._isGooglePayAvailable$.next(value);
  }

  private readonly _isApplePayAvailable$ = new BehaviorSubject(false);
  readonly isApplePayAvailable$ = this._isApplePayAvailable$.asObservable();

  get isApplePayAvailable(): boolean {
    return this._isApplePayAvailable$.getValue();
  }

  set isApplePayAvailable(value: boolean) {
    this._isApplePayAvailable$.next(value);
  }

  private readonly _paymentMethods$ = new BehaviorSubject<
    IPaymentMethodsResponse | undefined
  >(undefined);
  readonly paymentMethods$ = this._paymentMethods$.asObservable();

  get paymentMethods(): IPaymentMethodsResponse | undefined {
    return this._paymentMethods$.getValue();
  }

  set paymentMethods(value: IPaymentMethodsResponse | undefined) {
    this._paymentMethods$.next(value);
  }

  readonly availablePaymentMethods$ = combineLatest([
    this.isApplePayAvailable$,
    this.isGooglePayAvailable$
  ]).pipe(
    map(([isApplePayAvailable, isGooglePayAvailable]) => {
      return [
        ...(isApplePayAvailable ? [AdyenPaymentMethod.ApplePay] : []),
        ...(isGooglePayAvailable ? [AdyenPaymentMethod.GooglePay] : []),
        AdyenPaymentMethod.CreditCard
      ];
    })
  );

  readonly defaultPaymentMethod$: Observable<AdyenPaymentMethod | undefined> =
    combineLatest([this.isApplePayAvailable$, this.isGooglePayAvailable$]).pipe(
      map(([isApplePayAvailable, isGooglePayAvailable]) => {
        const isAppleOs = ['iOS', 'Mac OS'].includes(detect()?.os ?? '');
        if (isAppleOs && isApplePayAvailable) {
          return AdyenPaymentMethod.ApplePay;
        } else if (isGooglePayAvailable) {
          return AdyenPaymentMethod.GooglePay;
        } else {
          return AdyenPaymentMethod.CreditCard;
        }
      })
    );

  readonly hasMultiplePaymentMethods$: Observable<boolean> =
    this.availablePaymentMethods$.pipe(
      map((availablePaymentMethods) => availablePaymentMethods.length > 1)
    );
}
